import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import {
  getAdminSubscriptionPlans, createSubscriptionPlan, updateSubscriptionPlan, deleteSubscriptionPlan,
  getAdminSmsPackages, createSmsPackage, updateSmsPackage, deleteSmsPackage
} from '../../services/api';
import { toast } from 'sonner';

const fmt = (p) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(p);

export const AdminPricingPanel = () => {
  const [subPlans, setSubPlans] = useState([]);
  const [smsPackages, setSmsPackages] = useState([]);
  const [planModal, setPlanModal] = useState(null);
  const [pkgModal, setPkgModal] = useState(null);
  const [planForm, setPlanForm] = useState({ name: '', type: 'monthly', price: '', discounted_price: '' });
  const [pkgForm, setPkgForm] = useState({ name: '', sms_count: '', price: '', discounted_price: '' });

  useEffect(() => {
    getAdminSubscriptionPlans().then(r => setSubPlans(r.data)).catch(() => {});
    getAdminSmsPackages().then(r => setSmsPackages(r.data)).catch(() => {});
  }, []);

  const handleSavePlan = async () => {
    try {
      if (planModal?.id) { await updateSubscriptionPlan(planModal.id, planForm); toast.success('Plan güncellendi'); }
      else { await createSubscriptionPlan(planForm); toast.success('Plan oluşturuldu'); }
      setPlanModal(null);
      getAdminSubscriptionPlans().then(r => setSubPlans(r.data));
    } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
  };

  const handleSavePkg = async () => {
    try {
      if (pkgModal?.id) { await updateSmsPackage(pkgModal.id, pkgForm); toast.success('Paket güncellendi'); }
      else { await createSmsPackage(pkgForm); toast.success('Paket oluşturuldu'); }
      setPkgModal(null);
      getAdminSmsPackages().then(r => setSmsPackages(r.data));
    } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
  };

  return (
    <>
      <div className="space-y-6">
        {/* Subscription Plans */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[#8E8E93] uppercase">Abonelik Planları</h3>
            <Button size="sm" onClick={() => { setPlanForm({ name: '', type: 'monthly', price: '', discounted_price: '' }); setPlanModal({}); }}
              className="bg-[#6b6de3] rounded-full text-xs"><Plus size={14} className="mr-1" /> Plan Ekle</Button>
          </div>
          {subPlans.map(p => (
            <div key={p.id} className="bg-white rounded-2xl p-4 shadow-sm mb-2">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold text-gray-900">{p.name}</p>
                  <p className="text-xs text-[#8E8E93]">{p.type === 'monthly' ? 'Aylık (30 gün)' : 'Yıllık (365 gün)'}</p>
                  <div className="flex gap-3 mt-1">
                    <span className="text-xs">Fiyat: <b>{fmt(p.price)}</b></span>
                    {p.discounted_price > 0 && <span className="text-xs text-green-600">{'İ'}ndirimli: <b>{fmt(p.discounted_price)}</b></span>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => { setPlanForm({ name: p.name, type: p.type, price: p.price, discounted_price: p.discounted_price || '' }); setPlanModal(p); }}
                    className="p-2 rounded-lg hover:bg-gray-100 text-[#6b6de3]"><Pencil size={14} /></button>
                  <button onClick={() => { if (window.confirm('Silmek istediğinize emin misiniz?')) deleteSubscriptionPlan(p.id).then(() => { toast.success('Silindi'); getAdminSubscriptionPlans().then(r => setSubPlans(r.data)); }); }}
                    className="p-2 rounded-lg hover:bg-gray-100 text-[#FF3B30]"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* SMS Packages */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[#8E8E93] uppercase">SMS Paketleri</h3>
            <Button size="sm" onClick={() => { setPkgForm({ name: '', sms_count: '', price: '', discounted_price: '' }); setPkgModal({}); }}
              className="bg-[#34C759] hover:bg-green-600 rounded-full text-xs"><Plus size={14} className="mr-1" /> Paket Ekle</Button>
          </div>
          {smsPackages.map(p => (
            <div key={p.id} className="bg-white rounded-2xl p-4 shadow-sm mb-2">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold text-gray-900">{p.name}</p>
                  <p className="text-xs text-[#6b6de3] font-medium">{p.sms_count} SMS</p>
                  <div className="flex gap-3 mt-1">
                    <span className="text-xs">Fiyat: <b>{fmt(p.price)}</b></span>
                    {p.discounted_price > 0 && <span className="text-xs text-green-600">{'İ'}ndirimli: <b>{fmt(p.discounted_price)}</b></span>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => { setPkgForm({ name: p.name, sms_count: p.sms_count, price: p.price, discounted_price: p.discounted_price || '' }); setPkgModal(p); }}
                    className="p-2 rounded-lg hover:bg-gray-100 text-[#6b6de3]"><Pencil size={14} /></button>
                  <button onClick={() => { if (window.confirm('Silmek istediğinize emin misiniz?')) deleteSmsPackage(p.id).then(() => { toast.success('Silindi'); getAdminSmsPackages().then(r => setSmsPackages(r.data)); }); }}
                    className="p-2 rounded-lg hover:bg-gray-100 text-[#FF3B30]"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Plan Modal */}
      <Dialog open={!!planModal} onOpenChange={o => !o && setPlanModal(null)}>
        <DialogContent className="sm:rounded-2xl sm:max-w-md lg:max-w-3xl sm:max-h-[75vh] overflow-hidden flex flex-col p-4 sm:p-6">
          <DialogHeader><DialogTitle>{planModal?.id ? 'Plan Düzenle' : 'Yeni Plan'}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <div><Label className="text-xs">Plan Adı</Label><Input value={planForm.name} onChange={e => setPlanForm(p => ({ ...p, name: e.target.value }))} className="h-12 bg-[#F2F2F7] border-0 rounded-xl" /></div>
            {!planModal?.id && <div><Label className="text-xs">Tip</Label>
              <Select value={planForm.type} onValueChange={v => setPlanForm(p => ({ ...p, type: v }))}>
                <SelectTrigger className="h-12 bg-[#F2F2F7] border-0 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="monthly">Aylık</SelectItem><SelectItem value="yearly">Yıllık</SelectItem></SelectContent>
              </Select></div>}
            <div><Label className="text-xs">Normal Fiyat (TL)</Label><Input type="number" value={planForm.price} onChange={e => setPlanForm(p => ({ ...p, price: e.target.value }))} className="h-12 bg-[#F2F2F7] border-0 rounded-xl" /></div>
            <div><Label className="text-xs">{'İ'}ndirimli Fiyat (TL)</Label><Input type="number" value={planForm.discounted_price} onChange={e => setPlanForm(p => ({ ...p, discounted_price: e.target.value }))} className="h-12 bg-[#F2F2F7] border-0 rounded-xl" /></div>
            <Button onClick={handleSavePlan} className="w-full bg-[#6b6de3] rounded-full">{planModal?.id ? 'Güncelle' : 'Oluştur'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* SMS Package Modal */}
      <Dialog open={!!pkgModal} onOpenChange={o => !o && setPkgModal(null)}>
        <DialogContent className="sm:rounded-2xl sm:max-w-md lg:max-w-3xl sm:max-h-[75vh] overflow-hidden flex flex-col p-4 sm:p-6">
          <DialogHeader><DialogTitle>{pkgModal?.id ? 'Paket Düzenle' : 'Yeni SMS Paketi'}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <div><Label className="text-xs">Paket Adı</Label><Input value={pkgForm.name} onChange={e => setPkgForm(p => ({ ...p, name: e.target.value }))} className="h-12 bg-[#F2F2F7] border-0 rounded-xl" /></div>
            <div><Label className="text-xs">SMS Sayısı</Label><Input type="number" value={pkgForm.sms_count} onChange={e => setPkgForm(p => ({ ...p, sms_count: e.target.value }))} className="h-12 bg-[#F2F2F7] border-0 rounded-xl" /></div>
            <div><Label className="text-xs">Normal Fiyat (TL)</Label><Input type="number" value={pkgForm.price} onChange={e => setPkgForm(p => ({ ...p, price: e.target.value }))} className="h-12 bg-[#F2F2F7] border-0 rounded-xl" /></div>
            <div><Label className="text-xs">{'İ'}ndirimli Fiyat (TL)</Label><Input type="number" value={pkgForm.discounted_price} onChange={e => setPkgForm(p => ({ ...p, discounted_price: e.target.value }))} className="h-12 bg-[#F2F2F7] border-0 rounded-xl" /></div>
            <Button onClick={handleSavePkg} className="w-full bg-[#34C759] hover:bg-green-600 rounded-full">{pkgModal?.id ? 'Güncelle' : 'Oluştur'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
